import { useMemo, memo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BreadcrumbsContainer,
  Breadcrumbs,
  useHeroSnackbar,
  useTranslations,
} from '@uniqkey-frontend/shared-app';
import PageRouteEnum from '../../../../enums/PageRouteEnum';
import {
  useGetOrganizationById,
  useGetOrganizationEmployeeAccountById,
  useGetGroupById,
} from '../../../../hooks/reactQuery';

interface IGroupOrganizationEmployeePageBreadcrumbsProps {
  groupId: string;
  organizationId: string;
  employeeAccountId: string;
}

const GroupOrganizationEmployeePageBreadcrumbs = (
  props: IGroupOrganizationEmployeePageBreadcrumbsProps,
) => {
  const { groupId, organizationId, employeeAccountId } = props;
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { showError } = useHeroSnackbar();

  const {
    data: group,
    isLoading: isGroupLoading,
    isError: isGroupError,
  } = useGetGroupById(
    { groupId },
  );

  const {
    data: organization,
    isLoading: isOrganizationLoading,
    isError: isOrganizationError,
  } = useGetOrganizationById(
    { organizationId },
  );

  const {
    data: employee,
    isLoading: isEmployeeLoading,
    isError: isEmployeeError,
  } = useGetOrganizationEmployeeAccountById(
    { employeeAccountId },
  );

  const isLoading = isGroupLoading || isOrganizationLoading || isEmployeeLoading;

  const breadcrumbsNodes = useMemo(() => {
    if (isLoading || !group || !organization || !employee) {
      return [];
    }
    return [
      {
        title: t('navigation.groups'),
        onClick: () => navigate(PageRouteEnum.Groups),
      },
      {
        title: t(group.name),
        onClick: () => navigate(`${PageRouteEnum.Groups}/${groupId}`),
      },
      {
        title: t(organization.name),
        onClick: () => navigate(`${PageRouteEnum.Groups}/${groupId}/${organizationId}`),
      },
      { title: employee.name },
    ];
  }, [isLoading, employee, organization, group, navigate, organizationId, groupId, t]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (isGroupError) {
      showError({ text: t('common.somethingWentWrong') });
      navigate(PageRouteEnum.Groups);
      return;
    }
    if (isOrganizationError) {
      showError({ text: t('common.somethingWentWrong') });
      navigate(`${PageRouteEnum.Groups}/${groupId}`);
      return;
    }
    if (isEmployeeError) {
      showError({ text: t('common.somethingWentWrong') });
      navigate(`${PageRouteEnum.Groups}/${groupId}/${organizationId}`);
    }
  }, [
    isLoading,
    isGroupError,
    isOrganizationError,
    isEmployeeError,
    organizationId,
    navigate,
    groupId,
    showError,
    t,
  ]);

  return (
    <BreadcrumbsContainer>
      <Breadcrumbs
        nodes={breadcrumbsNodes}
        isLoading={isLoading}
      />
    </BreadcrumbsContainer>
  );
};

export default memo(GroupOrganizationEmployeePageBreadcrumbs);
